import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  MailIcon,
  DeviceMobileIcon,
  CalendarIcon,
  IdentificationIcon,
} from "@heroicons/react/outline";
import CustomerServices from "../../services/CustomerServices";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function ViewVerificationEmail() {
  const id = new URLSearchParams(window.location.search).get("id");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRecord = async () => {
    setLoading(true);
    try {
      const resp = await CustomerServices.viewVerificationEmail(id);
      if (resp && resp.data) {
        setRecord(resp.data);
      } else {
        setError(resp?.message || "Verification email not found");
      }
    } catch (err) {
      setError("Failed to fetch verification email details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const SectionCard = ({ title, children, className = "" }) => (
    <div
      className={`bg-white shadow-sm border border-gray-200 rounded-lg ${className}`}
    >
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );

  const DetailRow = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {value || "N/A"}
      </dd>
    </div>
  );

  if (loading) {
    return (
      <Layout title="Loading..." pageTag="customer" current="/verification-emails">
        <div className="flex justify-center items-center h-64">
          <HollowDotsSpinner color="#4A90E2" />
        </div>
      </Layout>
    );
  }

  if (error || !record) {
    return (
      <Layout
        title="Verification Email Not Found"
        pageTag="customer"
        current="/verification-emails"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <MailIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Verification Email Not Found
            </h3>
            <p className="text-gray-500 mb-4">
              {error || "The requested verification email could not be found."}
            </p>
            <Link
              to="/verification-emails"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Verification Emails
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const deviceName = [record.deviceBrand, record.deviceModel]
    .filter(Boolean)
    .join(" ");

  return (
    <Layout
      title={`${record.userName || record.email || "Verification Email"} - Details`}
      pageTag="customer"
      current="/verification-emails"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/verification-emails"
          className="inline-flex items-center text-sm text-red-600 hover:text-red-500 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Verification Emails
        </Link>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center">
                  <MailIcon className="h-12 w-12 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    {record.userName || "—"}
                  </h1>
                  <p className="text-red-100 mt-1">{record.email}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <SectionCard title="Verification Information">
                <dl className="divide-y divide-gray-200">
                  <DetailRow label="Email" value={record.email} />
                  <DetailRow label="User Name" value={record.userName} />
                  <DetailRow
                    label="Phone Number"
                    value={record.phoneNumber}
                  />
                  <DetailRow label="Created By" value={record.createdBy} />
                  <DetailRow label="Created On" value={record.createdOn} />
                </dl>
              </SectionCard>

              <SectionCard title="Identity Details">
                <dl className="divide-y divide-gray-200">
                  <DetailRow label="BVN" value={record.bvn} />
                  <DetailRow label="NIN" value={record.nin} />
                </dl>
              </SectionCard>

              <SectionCard title="Device Information">
                <dl className="divide-y divide-gray-200">
                  <DetailRow label="Device ID" value={record.deviceID} />
                  <DetailRow label="Device Make" value={record.deviceMake} />
                  <DetailRow label="Device Brand" value={record.deviceBrand} />
                  <DetailRow label="Device Model" value={record.deviceModel} />
                  <DetailRow label="Model Name" value={record.deviceModelName} />
                  <DetailRow label="Device Type" value={record.deviceType} />
                  <DetailRow label="OS Version" value={record.osVersion} />
                </dl>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <SectionCard title="Details">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <IdentificationIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">
                        Verification Code
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {record.code || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DeviceMobileIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">Device</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {deviceName || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-700">Created On</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {record.createdOn || "N/A"}
                    </span>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}